import {skipToken} from '@reduxjs/toolkit/query';
import {
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, Hide, VStack} from 'platform/foundation';
import * as Yup from 'yup';

import {isNotNil} from 'ramda';

import {SeriesType, useGetBranchListQuery, useGetSeriesListQuery} from '@dms/api/core';
import {
  useCreateWorkshopInterestSeriesDefinitionMutation,
  useGetWorkshopInterestSeriesDefinitionQuery,
  usePutWorkshopInterestSeriesDefinitionMutation,
  WorkshopInterestSeriesDefinitionResponseBody,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {getOptionsFromSeries} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

interface WorkshopInterestSeriesDefinitionFormProps extends TestIdProps {
  definitionId?: WorkshopInterestSeriesDefinitionResponseBody['id'];
}

const WORKSHOP_INTEREST_SERIES_TYPE: SeriesType = 'workshop/workshop-interest';

const getSchema = (isSystemDefinition: boolean) =>
  Yup.object({
    name: Yup.string().required(),
    branchId: isSystemDefinition
      ? Yup.string().nullable().default(null)
      : Yup.string().nullable().required(),
    seriesId: Yup.string().required(),
  });

type FormValues = Yup.InferType<ReturnType<typeof getSchema>>;

export function WorkshopInterestSeriesDefinitionForm(
  props: WorkshopInterestSeriesDefinitionFormProps
) {
  const {data: branches} = useGetBranchListQuery();

  const {data: series} = useGetSeriesListQuery({
    type: [WORKSHOP_INTEREST_SERIES_TYPE],
  });

  const {
    data: definition,
    isFetching: isFetchingDefinition,
    isError: isErrorDefinition,
  } = useGetWorkshopInterestSeriesDefinitionQuery(
    isNotNil(props.definitionId) ? {seriesDefinitionId: props.definitionId} : skipToken
  );

  const [saveSeriesDefinition, {isLoading: isLoadingSave}] =
    usePutWorkshopInterestSeriesDefinitionMutation();

  const [createSeriesDefinition, {isLoading: isLoadingCreate}] =
    useCreateWorkshopInterestSeriesDefinitionMutation();

  const workshopInterestSeries = getOptionsFromSeries(series, WORKSHOP_INTEREST_SERIES_TYPE);

  const isSystemDefinition = Boolean(definition?.isSystem && props.definitionId);
  const schema = getSchema(isSystemDefinition);

  const branchListOptions =
    branches?.branchListItems.map((branch) => ({
      value: branch.id,
      label: branch.marketingName,
    })) ?? [];

  const handleSubmit: FormSubmitHandler<FormValues> = (values) => {
    if (props.definitionId && definition?.id) {
      return saveSeriesDefinition({
        seriesDefinitionId: definition.id,
        putWorkshopInterestSeriesDefinitionRequestBody: values,
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
          closeCurrentDialog();
        })
        .catch(handleApiError);
    }

    return createSeriesDefinition({
      createWorkshopInterestSeriesDefinitionRequestBody: {
        name: values.name,
        seriesId: values.seriesId,
        branchId: values.branchId ?? '',
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isFetchingDefinition} isError={isErrorDefinition} spacing={5}>
      <Form<FormValues>
        onSubmit={handleSubmit}
        schema={schema}
        defaultValues={{
          name: definition?.name ?? '',
          branchId: definition?.branchId ?? null,
          seriesId: definition?.seriesId ?? '',
        }}
      >
        {(control) => (
          <VStack spacing={4}>
            <Card title={i18n.t('entity.settings.labels.general')} variant="inlineGrey">
              <Grid columns={2}>
                <FormField
                  control={control}
                  name="name"
                  type="text"
                  label={i18n.t('entity.seriesDefinition.labels.definitionName')}
                  data-testid={suffixTestId('definitionName', props)}
                  isRequired
                />
                <GridItem>
                  <Hide when={definition?.isSystem}>
                    <FormField
                      control={control}
                      type="choice"
                      name="branchId"
                      label={i18n.t('entity.branch.labels.branch')}
                      data-testid={suffixTestId('branchId', props)}
                      options={branchListOptions}
                      menuInPortal
                      isRequired
                    />
                  </Hide>
                </GridItem>
                <FormField
                  control={control}
                  type="choice"
                  name="seriesId"
                  label={i18n.t('entity.workshopInterestSeriesDefinition.labels.seriesId')}
                  data-testid={suffixTestId('seriesId', props)}
                  options={workshopInterestSeries}
                  menuInPortal
                  isRequired
                />
              </Grid>
            </Card>

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.cancel')}
                variant="secondary"
                data-testid={suffixTestId('cancel', props)}
                onClick={closeCurrentDialog}
              />
              <FormButton
                control={control}
                isLoading={isLoadingSave || isLoadingCreate}
                type="submit"
                data-testid={suffixTestId('submit', props)}
                title={i18n.t(
                  props.definitionId ? 'general.actions.save' : 'general.actions.create'
                )}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
